if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.ownartDots = this.querySelector('[data-ownart-media-dots]');
        this.ownartMediaList = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        this.setupOwnartMediaDots();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.setActiveOwnartDot(event.detail.currentElement.dataset.mediaId);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.setActiveOwnartDot(mediaId);
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.ownartDots && this.mql.matches) {
            activeMedia.parentElement.scrollTo({ top: activeMedia.offsetTop, behavior: 'smooth' });
          } else if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setupOwnartMediaDots() {
        if (!this.ownartDots || !this.ownartMediaList) return;

        this.ownartDots.querySelectorAll('[data-ownart-media-target]').forEach((button) => {
          button.addEventListener('click', () => {
            this.setActiveOwnartDot(button.dataset.ownartMediaTarget);
            this.setActiveMedia(button.dataset.ownartMediaTarget, false);
          });
        });

        this.setupOwnartLoopScroll();
        this.ownartMediaList.addEventListener(
          'scroll',
          debounce(() => {
            const mediaItems = Array.from(this.ownartMediaList.querySelectorAll('.product__media-item[data-media-id]'));
            if (!mediaItems.length) return;

            const listTop = this.ownartMediaList.getBoundingClientRect().top;
            const activeItem = mediaItems.reduce((closest, item) => {
              const closestDistance = Math.abs(closest.getBoundingClientRect().top - listTop);
              const itemDistance = Math.abs(item.getBoundingClientRect().top - listTop);
              return itemDistance < closestDistance ? item : closest;
            });
            this.setActiveOwnartDot(activeItem.dataset.mediaId);
          }, 80)
        );
      }

      setupOwnartLoopScroll() {
        if (!this.ownartMediaList) return;

        this.ownartMediaList.addEventListener(
          'wheel',
          (event) => {
            if (!this.ownartDots || !this.mql.matches) return;

            const mediaItems = Array.from(this.ownartMediaList.querySelectorAll('.product__media-item[data-media-id]'));
            if (mediaItems.length < 2) return;

            const maxScrollTop = this.ownartMediaList.scrollHeight - this.ownartMediaList.clientHeight;
            const isAtBottom = this.ownartMediaList.scrollTop >= maxScrollTop - 2;
            const isAtTop = this.ownartMediaList.scrollTop <= 2;

            if (event.deltaY > 0 && isAtBottom) {
              event.preventDefault();
              this.ownartMediaList.scrollTo({ top: 0, behavior: 'smooth' });
              this.setActiveOwnartDot(mediaItems[0].dataset.mediaId);
            }

            if (event.deltaY < 0 && isAtTop) {
              event.preventDefault();
              const lastMediaItem = mediaItems[mediaItems.length - 1];
              this.ownartMediaList.scrollTo({ top: lastMediaItem.offsetTop, behavior: 'smooth' });
              this.setActiveOwnartDot(lastMediaItem.dataset.mediaId);
            }
          },
          { passive: false }
        );
      }

      setActiveOwnartDot(mediaId) {
        if (!this.ownartDots || !mediaId) return;

        let matched = false;
        this.ownartDots.querySelectorAll('[data-ownart-media-target]').forEach((button) => {
          if (button.dataset.ownartMediaTarget === mediaId) {
            button.setAttribute('aria-current', 'true');
            matched = true;
          } else {
            button.removeAttribute('aria-current');
          }
        });

        if (!matched) {
          const activeMedia = this.ownartMediaList?.querySelector('.product__media-item.is-active[data-media-id]');
          const fallbackMediaId = activeMedia?.dataset?.mediaId;
          if (fallbackMediaId && fallbackMediaId !== mediaId) {
            this.setActiveOwnartDot(fallbackMediaId);
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
